import React from "react";
import { Typography } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { dataStreamLabel } from "@utils/data_access";
import type { DataStream } from "@utils/data_access";

type PermissionsManager = { first_name: string; last_name: string };

export type DataAccessDeniedProps = {
  /** Streams the viewer is missing. Named in the message so the ask is actionable. */
  streams: DataStream[];
  /** Use "inline" inside a tab or panel; "page" fills the viewport. */
  variant?: "page" | "inline";
  className?: string;
  style?: React.CSSProperties;
};

const joinLabels = (streams: DataStream[]): string => {
  const labels = streams.map(dataStreamLabel);
  if (labels.length <= 1) return labels[0] ?? "this";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
};

/**
 * Shown in place of content the viewer lacks data-access permission for.
 *
 * Matches the Sandbox denial state so every locked surface reads the same way and
 * points at the person who can actually grant access.
 */
export const DataAccessDenied = ({
  streams,
  variant = "page",
  className,
  style = {},
}: DataAccessDeniedProps) => {
  const permissionsManagerList: PermissionsManager[] = useSelector(
    ({ team }: any) => team?.info?.permissions_manager_list ?? []
  );

  const label = joinLabels(streams);
  const plural = streams.length > 1;

  return (
    <div
      className={className}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: variant === "page" ? "60vh" : undefined,
        padding: variant === "inline" ? "48px 16px" : undefined,
        gap: 12,
        textAlign: "center",
        ...style,
      }}
    >
      <LockOutlined style={{ fontSize: 40, color: "#bfbfbf" }} />
      <Typography.Title level={4} style={{ margin: 0 }}>
        {label} data access required
      </Typography.Title>
      <Typography.Text type="secondary">
        You do not have permission to view {label} data.
        <br />
        Please reach out to your team&apos;s permissions manager to request access.
      </Typography.Text>
      {permissionsManagerList.length > 0 && (
        <Typography.Text>
          Permissions Manager{permissionsManagerList.length > 1 ? "s" : ""}:{" "}
          <strong>
            {permissionsManagerList
              .map((u) => `${u.first_name} ${u.last_name}`)
              .join(", ")}
          </strong>
        </Typography.Text>
      )}
      {plural && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          This view needs all of the listed data types.
        </Typography.Text>
      )}
    </div>
  );
};

export default DataAccessDenied;
